'use client';

import React from 'react';
import type { ReactNode } from 'react';
import type { SelectedPersonalization } from './types';
import type { LeadGenerationInteractionProps } from 'components/analytics/types';
import { useSegmentTrack } from 'components/analytics/index';
import { useLeadGenerationInteraction } from 'components/analytics/hooks/useLeadGenerationInteraction';
import { ANALYTICS_EVENTS, LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { convertObjectToArray, getCookieValue } from 'components/analytics/utils';
import {
  AnalyticsModuleTrackingProvider,
  useAnalyticsModuleTracking,
} from 'components/analytics/AnalyticsModuleTracking';
import { getVariantDisplayName } from './getVariantDisplayName';
import { NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';
import { NinetailedInlineTracker } from 'components/shared/Ninetailed/NinetailedInlineTracker';
import { StatsigInlineTracker } from './StatsigInlineTracker';
import { logPipelineEvent } from './observability';

interface ExposureTrackerProps {
  /** Experiment selection for this module, if it participates in an experiment */
  exposure?: SelectedPersonalization;
  /** Contentful sys.id of the module being tracked */
  moduleId: string;
  /**
   * CSP nonce from request headers. Forwarded to the per-vendor inline
   * <script> trackers (Hawker `ninetailed_experience_viewed` and
   * `experiment_bucketed`) so they pass CSP and survive Meta in-app browser
   * hydration failures.
   */
  nonce?: string;
  /**
   * Whether the request has consented to Performance cookies (OneTrust C0002).
   * Read once per request in `getPageContext` and threaded down. When false,
   * the Statsig inline tracker (Hawker `experiment_bucketed` event + POST to
   * `/api/statsig-exposure`) is not rendered at all. The route handler and
   * `logStatsigExposure` still gate server-side as defense-in-depth.
   *
   * Defaults to `true` (opt-in) so callers that haven't been threaded through
   * `getPageContext` don't silently break analytics; the server-side route and
   * SDK gates remain the privacy enforcement of last resort.
   */
  hasPerformanceConsent?: boolean;
  children: ReactNode;
}

// Backward compatibility — Ninetailed experiences use NINETAILED_EXPERIENCE_VIEWED
// To remove when Ninetailed is deprecated
const NinetailedExposureInner: React.FC<{
  exposure: SelectedPersonalization;
  moduleId: string;
  nonce?: string;
}> = ({ exposure, moduleId, nonce }) => {
  const hasTracked = React.useRef(false);
  const { attributes } = useAnalyticsModuleTracking();

  const exposureData = React.useMemo(() => {
    const isPersonalization = exposure.experienceType === 'nt_personalization';
    const isExperiment = exposure.experienceType === 'nt_experiment';

    let variantDisplayName: string;
    if (isPersonalization) {
      variantDisplayName = exposure.variantName ?? `Variant ${exposure.variantIndex}`;
    } else if (isExperiment) {
      variantDisplayName = exposure.variantIndex === 0 ? 'Control' : `Variant ${exposure.variantIndex}`;
    } else {
      variantDisplayName = exposure.variantIndex === 0 ? 'Baseline' : `Variant ${exposure.variantIndex}`;
    }

    // Per-module variant_id:
    // - treatment: `variants[moduleId]` is the specific variant rendered for
    //   this module (the adapter populates one entry per module for
    //   multi-component experiments)
    // - control: module's own baseline.id → `moduleId` itself
    // - fallback: adapter's representative `variantId` (e.g. module not in
    //   fetched data)
    const perModuleVariantId = exposure.variants[moduleId];
    const variantId = perModuleVariantId ?? (exposure.variantIndex === 0 ? moduleId : (exposure.variantId ?? ''));

    return {
      ninetailed_id: getCookieValue(NINETAILED_ANONYMOUS_ID_COOKIE) ?? '',
      experience_name: exposure.experienceName ?? '',
      experience_id: exposure.experienceId,
      variant_name: variantDisplayName,
      variant_id: variantId,
      experience_type: exposure.experienceType ?? '',
    };
  }, [exposure, moduleId]);

  const analyticsProps: LeadGenerationInteractionProps = React.useMemo(
    () => ({
      interaction_type: LEAD_GENERATION_INTERACTION_TYPES.NINETAILED_EXPERIENCE_VIEWED,
      interaction_details: {
        location: attributes.moduleLocation,
        action_detail: 'experience_viewed',
      },
      additional_data: convertObjectToArray(exposureData),
    }),
    [exposureData, attributes.moduleLocation],
  );

  const trackEvent = useLeadGenerationInteraction(analyticsProps);

  React.useEffect(() => {
    if (!hasTracked.current) {
      trackEvent();
      hasTracked.current = true;
    }
  }, [trackEvent]);

  // Inline <script> fires the Hawker `ninetailed_experience_viewed` event
  // pre-hydration so it survives Meta in-app browser hydration failures.
  if (exposure.experienceType === undefined) {
    // Diagnostic: distinguishes "exposure never attached" (no log fires) from
    // "exposure attached but adapter enrichment didn't populate experienceType"
    // (this log fires). The latter silently zeroes out downstream Snowflake
    // event volume, which is what we observed during the V2 prod rollout.
    logPipelineEvent({
      event: 'exposureTracker.missingExperienceType',
      pipeline: 'v2',
      vendor: exposure.vendor,
      experimentId: exposure.experienceId,
      moduleId,
      variantIndex: exposure.variantIndex,
    });
    return null;
  }
  return (
    <NinetailedInlineTracker
      experienceId={exposureData.experience_id}
      experienceName={exposureData.experience_name}
      experienceSnowflakeName={null}
      variantId={exposureData.variant_id}
      variantName={exposure.variantName ?? ''}
      variantIndex={exposure.variantIndex}
      experienceType={exposure.experienceType}
      nonce={nonce}
    />
  );
};

const DefaultExposureInner: React.FC<{
  exposure: SelectedPersonalization;
  nonce?: string;
  hasPerformanceConsent: boolean;
}> = ({ exposure, nonce, hasPerformanceConsent }) => {
  const hasTracked = React.useRef(false);
  const { attributes } = useAnalyticsModuleTracking();

  const exposureData = React.useMemo(
    () => ({
      experiment_id: exposure.experienceId,
      variant_index: exposure.variantIndex,
      variant_id: exposure.variantId ?? '',
      variant_name: getVariantDisplayName(exposure.variantIndex),
    }),
    [exposure],
  );

  const analyticsProps: LeadGenerationInteractionProps = React.useMemo(
    () => ({
      interaction_type: LEAD_GENERATION_INTERACTION_TYPES.EXPERIMENT_VIEWED,
      interaction_details: {
        location: attributes.moduleLocation,
        action_detail: 'experience_viewed',
      },
      additional_data: convertObjectToArray(exposureData),
    }),
    [exposureData, attributes.moduleLocation],
  );

  const trackEvent = useSegmentTrack(
    {
      eventName: ANALYTICS_EVENTS.LEAD_GENERATION_INTERACTION,
      properties: analyticsProps,
      opts: { shouldSendToFacebook: false },
    },
    false,
  );

  React.useEffect(() => {
    if (!hasTracked.current) {
      trackEvent();
      hasTracked.current = true;
    }
  }, [trackEvent]);

  // Inline <script> fires the Hawker `experiment_bucketed` event AND posts to
  // /api/statsig-exposure pre-hydration so they survive Meta in-app browser
  // hydration failures. Skipped for non-Statsig fallthrough since those calls
  // are Statsig-specific.
  if (exposure.vendor !== 'statsig') {
    return null;
  }
  // Skip rendering the inline tracker entirely when the user lacks
  // Performance consent — no Hawker event, no POST, no payload on the wire.
  if (!hasPerformanceConsent) {
    return null;
  }
  // Prefer the readable Statsig group name ('control' / 'treatment-1' / …)
  // over the Contentful variant entry id so experiment_bucketed aligns with
  // the Statsig dashboard's source of truth.
  //
  // `additionalExposures` fires the inner Autotune exposure for the wrapper
  // pattern: the primary tracker logs the outer wrapping experiment (every
  // arm), and each extra tracker logs the bandit for users in the autotune arm
  // so it receives the exposures it needs to learn.
  return (
    <>
      <StatsigInlineTracker
        experimentKey={exposure.experienceId}
        variantKey={exposure.variantName ?? exposure.variantId ?? ''}
        nonce={nonce}
      />
      {exposure.additionalExposures?.map(extra => (
        <StatsigInlineTracker
          key={extra.experienceId}
          experimentKey={extra.experienceId}
          variantKey={extra.variantName}
          nonce={nonce}
        />
      ))}
    </>
  );
};

// Fires exposure events once on mount, not on viewport entry.
export const ExposureTracker: React.FC<ExposureTrackerProps> = ({
  exposure,
  moduleId,
  nonce,
  hasPerformanceConsent = true,
  children,
}) => {
  if (!exposure) return <>{children}</>;

  return (
    <AnalyticsModuleTrackingProvider>
      {exposure.vendor === 'ninetailed' ? (
        <NinetailedExposureInner exposure={exposure} moduleId={moduleId} nonce={nonce} />
      ) : (
        <DefaultExposureInner exposure={exposure} nonce={nonce} hasPerformanceConsent={hasPerformanceConsent} />
      )}
      {children}
    </AnalyticsModuleTrackingProvider>
  );
};
